import {
  Box,
  Chip,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import type { SessionEvent } from '@/types';
import { EVENT_TYPE_LABELS, formatMatchClock } from '@/types';
import { LIVE_PERIOD_LABELS, type LivePeriod } from '@/utils/liveClock';
import { formatCombinedZoneLabels } from '@/components/live/LiveActionZones';
import {
  groupLiveEvents,
  JUDGEMENT_STAT_COLORS,
  JUDGEMENT_STAT_LABELS,
  type JudgementStatKey,
} from '@/components/live/liveEventStats';

interface Props {
  events: SessionEvent[];
  canEdit?: boolean;
  newestFirst?: boolean;
  inverted?: boolean;
  onEdit?: (event: SessionEvent) => void;
  onDelete?: (event: SessionEvent) => void;
}

function judgementTone(value?: string | null): { label: string; color: string } {
  const key: JudgementStatKey =
    value === 'correct' || value === 'wrong' || value === 'uncertain' ? value : 'missing';
  return { label: JUDGEMENT_STAT_LABELS[key], color: JUDGEMENT_STAT_COLORS[key] };
}

function eventTypeLabel(event: SessionEvent): string {
  return EVENT_TYPE_LABELS[event.event_type as keyof typeof EVENT_TYPE_LABELS] || event.event_type;
}

function sortEventGroups(groups: SessionEvent[][], newestFirst: boolean): SessionEvent[][] {
  return [...groups].sort((a, b) => {
    const ta = a[0].match_second ?? 0;
    const tb = b[0].match_second ?? 0;
    if (ta !== tb) return newestFirst ? tb - ta : ta - tb;
    return newestFirst
      ? new Date(b[0].created_at).getTime() - new Date(a[0].created_at).getTime()
      : new Date(a[0].created_at).getTime() - new Date(b[0].created_at).getTime();
  });
}

export default function LiveEventVerticalTimeline({
  events,
  canEdit,
  newestFirst = true,
  inverted = false,
  onEdit,
  onDelete,
}: Props) {
  const groups = sortEventGroups(groupLiveEvents(events), newestFirst);

  const lineColor = inverted ? 'rgba(255,255,255,0.18)' : 'divider';
  const cardBorder = inverted ? 'rgba(255,255,255,0.14)' : 'divider';
  const cardBg = (highlight: boolean) =>
    inverted
      ? highlight
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(255,255,255,0.04)'
      : highlight
        ? 'action.hover'
        : 'background.paper';
  const dotBorder = inverted ? '#0f1419' : 'background.paper';
  const secondaryText = inverted ? 'rgba(255,255,255,0.55)' : 'text.secondary';
  const primaryText = inverted ? '#fff' : 'text.primary';

  if (groups.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center', color: secondaryText }}>
        <Typography>尚無事件記錄</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', pl: 2.5, pr: 0.5 }}>
      <Box
        sx={{
          position: 'absolute',
          left: 10,
          top: 8,
          bottom: 8,
          width: 2,
          bgcolor: lineColor,
          borderRadius: 1,
        }}
      />
      <Stack spacing={1.5}>
        {groups.map((group, index) => {
          const head = group[0];
          const tone = judgementTone(head.judgement);
          const periodLabel = head.period
            ? LIVE_PERIOD_LABELS[head.period as LivePeriod] || head.period
            : null;
          const zoneLine = formatCombinedZoneLabels(group.map((e) => e.event_type));
          const highlight = index === 0 && newestFirst;

          return (
            <Box key={group.map((e) => e.id).join('-')} sx={{ position: 'relative', display: 'flex', gap: 1.5 }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: -14,
                  top: 14,
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: tone.color,
                  border: '2px solid',
                  borderColor: dotBorder,
                  boxShadow: 1,
                  zIndex: 1,
                }}
              />
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  p: 1.25,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: cardBorder,
                  bgcolor: cardBg(highlight),
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 0.75 }}>
                      <Chip
                        label={formatMatchClock(head.match_second ?? 0)}
                        size="small"
                        color={inverted ? 'default' : 'secondary'}
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          ...(inverted
                            ? { bgcolor: 'rgba(255,255,255,0.12)', color: '#fff' }
                            : {}),
                        }}
                      />
                      {periodLabel && (
                        <Chip
                          label={periodLabel}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 22,
                            ...(inverted
                              ? { borderColor: 'rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.85)' }
                              : {}),
                          }}
                        />
                      )}
                      <Chip
                        label={tone.label}
                        size="small"
                        sx={{
                          height: 22,
                          bgcolor: `${tone.color}18`,
                          color: tone.color,
                          fontWeight: 700,
                        }}
                      />
                    </Stack>

                    <Stack spacing={0.25} sx={{ mb: 0.5 }}>
                      {group.map((event) => (
                        <Stack
                          key={event.id}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={0.5}
                        >
                          <Typography variant="body2" fontWeight={800} sx={{ color: primaryText }}>
                            {eventTypeLabel(event)}
                          </Typography>
                          {canEdit && (
                            <Stack direction="row" flexShrink={0}>
                              <IconButton
                                size="small"
                                onClick={() => onEdit?.(event)}
                                aria-label="編輯"
                                sx={inverted ? { color: 'rgba(255,255,255,0.7)' } : undefined}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => onDelete?.(event)}
                                aria-label="刪除"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          )}
                        </Stack>
                      ))}
                    </Stack>

                    <Typography variant="caption" sx={{ color: secondaryText }}>
                      {zoneLine}
                    </Typography>

                    {group.some((e) => e.note) && (
                      <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                        {group
                          .filter((e) => e.note)
                          .map((event) => (
                            <Typography key={event.id} variant="body2" sx={{ color: primaryText }}>
                              {event.note}
                            </Typography>
                          ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
